import React, { useRef, useState, useEffect } from 'react';
import IframeModal from './IframeModal';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClickOutside } from '../hooks/useClickOutside';
import UserProfileMenu from './project/UserProfileMenu';
import PlusIcon from './icons/PlusIcon.js';
import CreateWorkspaceModal from './CreateWorkspaceModal.js';
import AddProjectModal from './AddProjectModal.js';
import AddSaveModal from './AddSaveModal.js';
import AddBookmarkModal from './bookmark/AddBookmarkModal.js';
import AddPlanModal from './AddPlanModal.js';
import PlanIcon from './icons/PlanIcon.js';
import SaveIcon from './icons/SaveIcon.js';
import { BookmarkIcon } from './icons/BookmarkIcon.js';
import ProjectIcon from './icons/ProjectIcon.js';
import WorkspaceIcon from './icons/WorkspaceIcon.js';
import { useSaves } from '../hooks/useSaves'; // Import useSaves
import {
    NavbarContainer,
    Logo,
    NavLinks,
    NavLink,
    UserSection,
    UserButton,
    UserMenu,
    MenuItem,
    UserAvatar,
    AddMenuButton,
    AddMenu,
    AddMenuItem
} from '../styles/Navbar.styles';
import { updateProject } from '../utils/projectHandlers';
import { updateUserSettings } from '../utils/firebaseUtils';

function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [showMenu, setShowMenu] = useState(false);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [activeModal, setActiveModal] = useState(null);
    const [isIframeModalOpen, setIsIframeModalOpen] = useState(false);
    const [showSettingsMenu, setShowSettingsMenu] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [addMenuStyle, setAddMenuStyle] = useState({});
    const menuRef = useRef();
    const addMenuRef = useRef();
    const addMenuButtonRef = useRef();

    // Fetch allUniqueTags and addSave function using useSaves hook
    const { allUniqueTags, addSave, allSaves } = useSaves(user?.uid);

    useClickOutside(menuRef, () => setShowMenu(false));
    useClickOutside(addMenuRef, () => {
        if (showAddMenu) {
            setShowAddMenu(false);
        }
    });

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleAddMenu = () => {
        const isOpening = !showAddMenu;
        setShowAddMenu(isOpening);

        if (isOpening) {
            // Calculate position after a brief delay to allow rendering
            setTimeout(() => {
                if (addMenuRef.current && addMenuButtonRef.current) {
                    const menuRect = addMenuRef.current.getBoundingClientRect();
                    const buttonRect = addMenuButtonRef.current.getBoundingClientRect();
                    const viewportWidth = window.innerWidth;

                    // Check if the menu overflows the right edge of the viewport
                    // The menu is by default left-aligned with the button
                    let style = {};
                    let alignment = 'left'; // Default alignment
                    if (buttonRect.left + menuRect.width > viewportWidth) {
                        style = { right: 0 };
                        alignment = 'right';
                    } else {
                        style = { left: 0 };
                    }
                    // Pass alignment to style for styled-component, or set another state for it
                    // For simplicity here, let's assume AddMenu can take an alignment prop
                    // Or we can adjust the style object directly if AddMenu handles it via props
                    setAddMenuStyle({ ...style, menuAlignment: alignment });
                }
            }, 0);
        }
    };

    const handleAddMenuClick = (modalType) => {
        setActiveModal(modalType);
        setShowAddMenu(false); // Close menu after selection
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Failed to log out', error);
        }
    };

    return (
        <>
        <NavbarContainer>
            <Logo to="/">Goalscape</Logo>
            <NavLinks>
                <div style={{ position: 'relative' }}>
                    <AddMenuButton ref={addMenuButtonRef} onClick={toggleAddMenu}>
                        <PlusIcon color="white" />
                    </AddMenuButton>
                    {showAddMenu && (
                        <AddMenu ref={addMenuRef} style={addMenuStyle}>
                            <AddMenuItem onClick={() => handleAddMenuClick('plan')}>
                                <PlanIcon color="#FF69B4" /> New Plan
                            </AddMenuItem>
                            <AddMenuItem onClick={() => handleAddMenuClick('save')}>
                                <SaveIcon color="#1E90FF" /> New Save
                            </AddMenuItem>
                            <AddMenuItem onClick={() => handleAddMenuClick('bookmark')}>
                                <BookmarkIcon color="#8A2BE2" /> New Bookmark
                            </AddMenuItem>
                            <AddMenuItem onClick={() => handleAddMenuClick('project')}>
                                <ProjectIcon color="#32CD32" /> New Project
                            </AddMenuItem>
                            <AddMenuItem onClick={() => handleAddMenuClick('workspace')}>
                                <WorkspaceIcon color="#FFD700" /> New Workspace
                            </AddMenuItem>
                        </AddMenu>
                    )}
                </div>
                {!isMobile && (
                    <>
                        <NavLink onClick={() => setIsIframeModalOpen(!isIframeModalOpen)}>AI</NavLink>
                        <NavLink to="/">Dashboard</NavLink>
                    </>
                )}
            </NavLinks>
            {user && (
                <UserSection>
                    <UserButton onClick={() => setShowMenu(!showMenu)}>
                        {user.photoURL ? (
                            <UserAvatar src={user.photoURL} alt={user.displayName} />
                        ) : (
                            <UserAvatar as="div">
                                {user.displayName?.charAt(0) || user.email?.charAt(0)}
                            </UserAvatar>
                        )}
                    </UserButton>
                    {showMenu && (
                        <UserMenu ref={menuRef}>
                            <MenuItem disabled>
                                Signed in as <strong>{user.displayName || user.email}</strong>
                            </MenuItem>
                            {isMobile && (
                                <>
                                    <MenuItem onClick={() => { setIsIframeModalOpen(!isIframeModalOpen); setShowMenu(false); }}>AI</MenuItem>
                                    <MenuItem as={Link} to="/" onClick={() => setShowMenu(false)}>Dashboard</MenuItem>
                                </>
                            )}
                            <MenuItem onClick={() => setShowSettingsMenu(!showSettingsMenu)}>Settings</MenuItem>
                            {showSettingsMenu && (
                            <UserProfileMenu onBackgroundChange={async (bg) => {
                                if (user) {
                                    await updateUserSettings(user.uid, { background: bg });
                                }
                            }}/>
                            )}
                            <MenuItem onClick={handleLogout}>Sign Out</MenuItem>
                        </UserMenu>
                    )}
                </UserSection>
            )}
        </NavbarContainer>
            <IframeModal isOpen={isIframeModalOpen} onClose={() => setIsIframeModalOpen(false)} iframeSrc="https://akmaldju-librechat.hf.space" />
            {activeModal === 'workspace' && <CreateWorkspaceModal onClose={() => setActiveModal(null)} />}
            {activeModal === 'project' && <AddProjectModal onClose={() => setActiveModal(null)} />}
            {activeModal === 'save' && <AddSaveModal isOpen={true} onClose={() => setActiveModal(null)} userId={user?.uid} allUniqueTags={allUniqueTags} addSave={addSave} allSaves={allSaves} />}
            {activeModal === 'bookmark' && <AddBookmarkModal isOpen={true} onClose={() => setActiveModal(null)} userId={user?.uid} />}
            {activeModal === 'plan' && <AddPlanModal isOpen={true} onClose={() => setActiveModal(null)} />}
        </>
    );
}

export default Navbar;
